/**
 * Does Firestore still have unsynced writes?
 *
 * This is the state worth showing a user, and it is not the same as being
 * offline: a write made in a tunnel is queued and durable, and the only useful
 * message is "saved on this phone, not yet on the server". Showing a network
 * icon instead invites the interpretation "it did not save", which is false and
 * which makes people type the weight in twice.
 *
 * `waitForPendingWrites()` resolves when the queue drains. It never rejects for
 * being offline — it simply does not resolve until there is a network again,
 * which is exactly the semantics wanted here.
 */
import { waitForPendingWrites } from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { db } from '@/firebase.ts';

export const usePendingWrites = (): boolean => {
  const [pending, setPending] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const watch = () => {
      setPending(true);
      void waitForPendingWrites(db).then(() => {
        if (!cancelled) setPending(false);
      });
    };

    watch();
    // Re-arm whenever the app is brought back to the foreground: a promise
    // created before a long backgrounding may have been settled by a sync the
    // page never observed.
    document.addEventListener('visibilitychange', watch);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', watch);
    };
  }, []);

  return pending;
};
